import { useEffect, useMemo } from 'react';
import { Modal } from 'antd';

// Extend Window interface for unsaved changes detection
declare global {
  interface Window {
    hasUnsavedChanges?: boolean;
    showUnsavedChangesModal?: () => Promise<boolean>;
  }
}

interface UseUnsavedChangesOptions {
  hasChanges: boolean;
  data?: any;
  initialData?: any;
  modalTitle?: string;
  modalContent?: string;
  okText?: string;
  cancelText?: string;
}

/**
 * Custom hook for handling unsaved changes detection and navigation guards
 * @param options Configuration options for the unsaved changes detection
 * @returns boolean indicating if there are unsaved changes
 */
export const useUnsavedChanges = ({
  hasChanges,
  data,
  initialData,
  modalTitle = 'Unsaved Changes',
  modalContent = 'You have unsaved changes. If you leave now, they will be lost. Do you want to continue?',
  okText = 'Yes',
  cancelText = 'No'
}: UseUnsavedChangesOptions): boolean => {
  const hasUnsavedChanges = useMemo(() => {
    if (data && initialData) {
      const dataChanged = JSON.stringify(data) !== JSON.stringify(initialData);
      return dataChanged || hasChanges;
    }
    return hasChanges;
  }, [hasChanges, data, initialData]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      window.hasUnsavedChanges = true;
      window.showUnsavedChangesModal = () => {
        return new Promise((resolve) => {
          Modal.confirm({
            title: modalTitle,
            content: modalContent,
            okText,
            cancelText,
            okType: 'danger',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
      };
    } else {
      window.hasUnsavedChanges = false;
      window.showUnsavedChangesModal = undefined;
    }

    return () => {
      window.hasUnsavedChanges = false;
      window.showUnsavedChangesModal = undefined;
    };
  }, [hasUnsavedChanges, modalTitle, modalContent, okText, cancelText]);

  return hasUnsavedChanges;
};
